import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { hydrate, QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient } from '@zg-rentals/ts-rent-payment-client';

import { useLocation } from 'react-router-dom';
import { bootstrap } from './bootstrap';
import { createRoot } from 'react-dom/client';
import { connect, Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import cx from 'classnames';
import ThemeProvider from '@zg-rentals/multibrand-ui/lib/ThemeProvider';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { CometTheme } from '@zillow/zrs-comet';
import { createAuthProvider } from '@zg-rentals/particles-js-auth-hooks';
import RentalManagerApp from '@rental-manager/pages/App';
import RenterHubApp from '@renter-hub/pages/App';
import configureStore from '@common/utils/configureStore';
import rootReducer from '@common/reducers';
import DebugContainer from './components/DebugContainer';
import { ZRMUtils } from '@zg-rentals/rental-manager-header-navigation';
import DevAppServer from '@dev-app/App';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { logError } from '@zg-rentals/log-error';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent } from './analytics';

setClient(getClient().extend({ prefixUrl: __DEV__ ? 'https://www.qa.zillow.net/rental' : '/rental' }));

bootstrap.register();

declare const window: any;

// Add fetch polyfill for browsers that don't support it
if (typeof fetch === 'undefined') {
  require('whatwg-fetch');
}

const history = createBrowserHistory();
const initialState = window.__INITIAL_STATE__;
const store = configureStore(initialState, history, rootReducer(history));

window.store = store;
const browserHistory = history;
window.browserHistory = history;

browserHistory.listen((location: any, action?: string) => {
  const navAction = action ?? (location as any)?.action;
  if (navAction === 'PUSH' || navAction === 'REPLACE') {
    window.scrollTo(0, 0);
  }
});

window.browserHistory.block((location, action) => {
  const { pathname: targetPath, search: targetSearchQueryParams, state = {} } = location;

  if (action === 'PUSH' && state.persistQueryParams !== true) {
    const currentSearchQueryParams = window.document.location.search;

    // add target search to whitelist
    const queryParamObj = ZRMUtils.queryUtils.parse(targetSearchQueryParams);
    const tempWhiteList = Object.keys(queryParamObj);

    // rebuild full path
    const fullPath = `${targetPath}${currentSearchQueryParams}${targetSearchQueryParams}`;

    // set path with persisted query params
    const targetNavPath = ZRMUtils.queryUtils.getPathWithPersistedQueryParams(fullPath, tempWhiteList);

    // timeout is required otherwise query param is not displayed in url
    setTimeout(() => {
      const currentState = { ...window.history.state, persistQueryParams: true };
      window.history.replaceState(currentState, document.title, targetNavPath);
    }, 0);
  }

  return true;
});

window.router = {
  redirectTo(t) {
    return browserHistory.replace(t);
  },
  transitionTo(t) {
    const currentState = { ...window.history.state, scrollTop: document.body.scrollTop };
    window.history.replaceState(currentState, document.title, window.location.href);
    return browserHistory.push(t);
  },
};

// FIXME: Admitingly not the greated approach. But we only produce a single client file.
// Might be better if webpack produced two entry points: rental - manager.js and renter - hub.js
// and we select one of these on the server
let ReactApp;
const pathName = window.location.pathname;
if (pathName.startsWith('/rental-manager/payments')) {
  ReactApp = RentalManagerApp;
} else if (pathName.startsWith('/renter-hub/payments')) {
  ReactApp = RenterHubApp;
} else if (__DEV__) {
  ReactApp = DevAppServer;
}

const getBrandClassName = (brand) =>
  cx({
    hpx: brand === 'hotpads',
    zsg: brand === 'zillow',
    txl: brand === 'trulia',
  });

const user = store.getState().user;

const AuthProvider = createAuthProvider({
  initialUser: user,
  initialSessionExpirationDate: user.sessionExpirationDate,
});

const Analytics = () => {
  const location = useLocation();
  useComscorePageTracking(() => trackComscoreEvent({}), location.pathname);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 30 * 1000,
    },
  },
});

if (window.__QUERY_CLIENT__) {
  hydrate(queryClient, window.__QUERY_CLIENT__);
}

const ClientApp = connect(({ app }: any) => ({ brand: app.brand }))((props: any) => {
  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });

  return (
    <AuthProvider>
      <ThemeProvider brand={props.brand}>
        <SCThemeProvider theme={CometTheme}>
          {/* @ts-ignore props missing children key */}
          <ConnectedRouter history={history}>
            <QueryClientProvider client={queryClient}>
              <Analytics />
              <div className={getBrandClassName(props.brand)}>
                {__DEV__ && <DebugContainer />}
                <ReactApp />
              </div>
            </QueryClientProvider>
          </ConnectedRouter>
        </SCThemeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
});

try {
  createRoot(document.getElementById('app-root')!).render(
    <RpErrorBoundary>
      <Provider store={store}>
        <ClientApp />
      </Provider>
    </RpErrorBoundary>,
  );
} catch (err) {
  logError({ error: err });
}
